import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { MapPin, Star, Phone, ShoppingCart, Plus, Package, ChevronDown, ChevronUp, Tag, Heart, Send, X, Navigation, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import { StaticMapPreview, NavigateToShopButton, LocationPickerModal, UserLocationBar, calcDistance } from '../components/LocationMap';
import API from '../utils/api';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { useLocation2 } from '../context/LocationContext';
import { toast } from 'react-toastify';

const CAT_EMOJI = { sand:'🏖️', cement:'🧱', iron:'⚙️', bricks:'🔶', long_bricks:'🟧', short_bricks:'🟫', other:'📦' };
const CAT_COLOR = { sand:'#F59E0B', cement:'#6B7280', iron:'#374151', bricks:'#DC2626', long_bricks:'#EA580C', short_bricks:'#92400E', other:'#6B7280' };

export default function ShopDetail() {
  const { id } = useParams();
  const [shop, setShop] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');
  const [expandedMat, setExpandedMat] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const { addToCart, cart } = useCart();
  const { toggle: toggleWishlist, isWishlisted } = useWishlist();
  const { user } = useAuth();
  const { location: userLocation, saveLocation } = useLocation2();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      API.get(`/owners/${id}`),
      API.get(`/materials?ownerId=${id}`),
      API.get(`/owners/${id}/reviews`)
    ]).then(([shopRes, matsRes, revRes]) => {
      setShop(shopRes.data);
      setMaterials(matsRes.data);
      setReviews(revRes.data.reviews || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const categories = [...new Set(materials.map(m => m.category))];
  const filtered = activeCategory ? materials.filter(m => m.category === activeCategory) : materials;
  const cartCount = (matId) => cart.filter(i => i.material._id === matId).reduce((s, i) => s + i.quantity, 0);

  const shopLat = shop?.location?.lat;
  const shopLng = shop?.location?.lng;
  const distance = userLocation && shopLat && shopLng
    ? calcDistance(userLocation.lat, userLocation.lng, shopLat, shopLng)
    : null;

  const handleAdd = (mat, variant = null) => {
    if (!user) { toast.info('Please login to add items'); navigate('/login'); return; }
    if (user.role !== 'customer') { toast.warning('Only customers can order'); return; }
    addToCart(mat, shop, 1, variant);
    toast.success(`${mat.name} added to cart!`, { autoClose: 1200 });
  };

  const handleWishlist = (mat) => {
    const wasWishlisted = isWishlisted(mat._id);
    toggleWishlist(mat, shop);
    toast.info(wasWishlisted ? 'Removed from wishlist' : 'Added to wishlist', { autoClose: 1000 });
  };

  const submitReview = async () => {
    if (!user) { toast.info('Login to submit a review'); navigate('/login'); return; }
    if (!myRating) { toast.error('Please select a rating'); return; }
    setSubmittingReview(true);
    try {
      const res = await API.post(`/owners/${id}/rate`, { rating: myRating, comment: myComment });
      setReviews(res.data.reviews || []);
      setShop(prev => ({ ...prev, rating: res.data.rating, totalRatings: res.data.totalRatings }));
      setShowReviewForm(false);
      setMyRating(0); setMyComment('');
      toast.success('Review submitted!');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit review'); }
    finally { setSubmittingReview(false); }
  };

  if (loading) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}><div className="spinner" /></div>;
  if (!shop) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}><Package size={48} color="var(--gray-border)" /></div>;

  return (
    <div style={{ minHeight:'100vh', background:'var(--page-bg)' }} className="page-with-bottom-nav">
      <Navbar />

      {/* Shop Header */}
      <div style={{ background:'white', borderBottom:'1px solid var(--gray-border)', padding:'18px 16px' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:14, flexWrap:'wrap' }}>
            <div style={{ width:60, height:60, background:'linear-gradient(135deg,var(--orange),var(--amber))', borderRadius:16, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:22, flexShrink:0 }}>
              {shop.shopName?.[0] || 'S'}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:5 }}>
                <h1 style={{ fontSize:19, fontWeight:700 }}>{shop.shopName}</h1>
                <span className={`badge ${shop.isOpen !== false ? 'badge-green' : 'badge-gray'}`}>{shop.isOpen !== false ? 'Open' : 'Closed'}</span>
                {shop.verificationStatus === 'approved' && <span className="badge badge-blue">✓ Verified</span>}
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:5 }}>
                <span style={{ display:'flex', alignItems:'center', gap:4, color:'var(--gray)', fontSize:13 }}>
                  <MapPin size={12} /> {shop.location?.address}, {shop.location?.city}, {shop.location?.state}
                </span>
                {shop.user?.phone && (
                  <a href={`tel:${shop.user.phone}`} style={{ display:'flex', alignItems:'center', gap:4, color:'var(--orange)', fontSize:13, fontWeight:600 }}>
                    <Phone size={12} /> {shop.user.phone}
                  </a>
                )}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                <button onClick={() => setShowReviewForm(!showReviewForm)}
                  style={{ display:'flex', alignItems:'center', gap:4, fontSize:13, fontWeight:600, color:'#F59E0B', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, padding:'4px 10px', cursor:'pointer' }}>
                  <Star size={12} fill="#F59E0B" /> {shop.rating?.toFixed(1) || '0'} ({shop.totalRatings || 0}) · Rate
                </button>
                {distance !== null && (
                  <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:13, color:'var(--gray)' }}>
                    <Navigation size={12} color="var(--blue)" />
                    ~{distance < 1 ? `${Math.round(distance*1000)}m` : `${distance.toFixed(1)}km`} away
                  </span>
                )}
              </div>
              {shop.description && <p style={{ color:'var(--gray)', fontSize:13, marginTop:8, lineHeight:1.5 }}>{shop.description}</p>}
            </div>
            {user?.role === 'customer' && (
              <button onClick={() => navigate('/cart')} className="btn-primary" style={{ flexShrink:0, padding:'8px 14px', fontSize:13 }}>
                <ShoppingCart size={14} /> Cart ({cart.length})
              </button>
            )}
          </div>

          {/* Offers */}
          {shop.offers?.length > 0 && (
            <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap' }}>
              {shop.offers.map((o, i) => (
                <div key={i} style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, padding:'5px 10px', fontSize:12, color:'#92400E', display:'flex', alignItems:'center', gap:4 }}>
                  <Tag size={10} /> {o.discount > 0 ? `${o.discount}% off` : ''} {o.title}
                </div>
              ))}
            </div>
          )}

          {/* Review Form */}
          {showReviewForm && (
            <div style={{ marginTop:14, background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:12, padding:14 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                <span style={{ fontWeight:700, fontSize:14 }}>Rate this shop</span>
                <button onClick={() => setShowReviewForm(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--gray)' }}><X size={16} /></button>
              </div>
              <div style={{ display:'flex', gap:4, marginBottom:10 }}>
                {[1,2,3,4,5].map(s => (
                  <button key={s} onClick={() => setMyRating(s)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:28, opacity: s <= myRating ? 1 : 0.25, transition:'opacity 0.1s' }}>★</button>
                ))}
              </div>
              <textarea className="form-input" rows={2} placeholder="Share your experience (optional)..."
                value={myComment} onChange={e => setMyComment(e.target.value)} style={{ resize:'none', marginBottom:10 }} />
              <button onClick={submitReview} className="btn-primary" style={{ padding:'9px 18px', fontSize:13 }} disabled={submittingReview}>
                <Send size={13} /> {submittingReview ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:'0 auto', padding:'18px 16px', display:'flex', flexDirection:'column' }}>

        {/* Location Section */}
        <div style={{ background:'white', borderRadius:14, padding:16, marginTop:16, boxShadow:'var(--shadow)', order:2 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, flexWrap:'wrap', gap:8 }}>
            <h3 style={{ fontWeight:700, fontSize:15, display:'flex', alignItems:'center', gap:7 }}>
              <MapPin size={15} color="var(--orange)" /> Shop Location & Directions
            </h3>
            <NavigateToShopButton shop={shop} userLocation={userLocation} />
          </div>

          {/* Your location bar */}
          <div style={{ marginBottom:12 }}>
            <UserLocationBar location={userLocation} onChangeLocation={() => setShowLocationPicker(true)} />
          </div>

          {/* Map Preview */}
          {shopLat && shopLng ? (
            <StaticMapPreview lat={shopLat} lng={shopLng} label={shop.shopName} height={200} />
          ) : (
            <div style={{ background:'var(--gray-light)', borderRadius:10, padding:'20px', textAlign:'center', color:'var(--gray)', fontSize:13 }}>
              <MapPin size={24} color="var(--gray-border)" style={{ marginBottom:6 }} />
              <div>Map not available — owner hasn't added GPS coordinates yet</div>
              <div style={{ fontSize:11, marginTop:4 }}>Address: {shop.location?.address}, {shop.location?.city}</div>
            </div>
          )}

          {/* Shop address details */}
          <div style={{ marginTop:10, display:'flex', flexWrap:'wrap', gap:10 }}>
            <div style={{ flex:1, minWidth:180, background:'var(--gray-light)', borderRadius:8, padding:'8px 12px' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--gray)', textTransform:'uppercase', marginBottom:3 }}>Shop Address</div>
              <div style={{ fontSize:13, fontWeight:600 }}>{shop.location?.address}</div>
              <div style={{ fontSize:12, color:'var(--gray)' }}>{shop.location?.city}, {shop.location?.state} - {shop.location?.pincode}</div>
            </div>
            {shopLat && shopLng && (
              <div style={{ flex:1, minWidth:140, background:'var(--gray-light)', borderRadius:8, padding:'8px 12px' }}>
                <div style={{ fontSize:10, fontWeight:700, color:'var(--gray)', textTransform:'uppercase', marginBottom:3 }}>GPS Coordinates</div>
                <div style={{ fontSize:12, fontWeight:600, fontFamily:'monospace' }}>{Number(shopLat).toFixed(5)}</div>
                <div style={{ fontSize:12, fontWeight:600, fontFamily:'monospace' }}>{Number(shopLng).toFixed(5)}</div>
              </div>
            )}
          </div>
        </div>

        {/* Main content: sidebar + materials */}
        <div className="shop-detail-layout" style={{ display:'grid', gridTemplateColumns:'180px 1fr', gap:16, alignItems:'start', order:1 }}>

          {/* Category Sidebar */}
          <div className="cat-sidebar" style={{ background:'white', borderRadius:12, padding:10, boxShadow:'var(--shadow)', position:'sticky', top:72, display:'flex', flexDirection:'column', gap:2 }}>
            <div className="cat-sidebar-title" style={{ fontWeight:700, fontSize:10, marginBottom:6, color:'var(--gray)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Categories</div>
            {[{key:'', label:'All Items'}, ...categories.map(c => ({key:c, label:c.replace('_',' ')}))].map(cat => (
              <button key={cat.key} onClick={() => setActiveCategory(cat.key)}
                style={{ padding:'7px 9px', borderRadius:7, border:'none', background: activeCategory === cat.key ? 'var(--orange-soft)' : 'transparent', color: activeCategory === cat.key ? 'var(--orange)' : 'var(--dark)', fontWeight: activeCategory === cat.key ? 600 : 400, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:5, textAlign:'left' }}>
                <span>{CAT_EMOJI[cat.key] || '📦'}</span>
                <span style={{ textTransform:'capitalize', flex:1 }}>{cat.label}</span>
                <span style={{ background:'var(--gray-light)', borderRadius:'50%', width:16, height:16, display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, flexShrink:0 }}>
                  {cat.key ? materials.filter(m => m.category === cat.key).length : materials.length}
                </span>
              </button>
            ))}
          </div>

          {/* Materials List */}
          <div className="shop-materials-panel" style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {filtered.length === 0 ? (
              <div style={{ textAlign:'center', padding:60, background:'white', borderRadius:14 }}>
                <Package size={40} color="var(--gray-border)" style={{ marginBottom:10 }} />
                <div style={{ fontWeight:700, fontSize:15 }}>No materials listed yet</div>
              </div>
            ) : (
              <div className="shop-materials-grid">
                {filtered.map(mat => (
                  <ShopMaterialCard key={mat._id} mat={mat}
                    expanded={expandedMat === mat._id}
                    onToggle={() => setExpandedMat(expandedMat === mat._id ? null : mat._id)}
                    onAdd={handleAdd} onWishlist={handleWishlist}
                    cartCount={cartCount(mat._id)} wishlisted={isWishlisted(mat._id)} />
                ))}
              </div>
            )}

            {/* Reviews */}
            {reviews.length > 0 && (
              <div style={{ background:'white', borderRadius:14, padding:18, boxShadow:'var(--shadow)', marginTop:6 }}>
                <h3 style={{ fontWeight:700, fontSize:15, marginBottom:14, display:'flex', alignItems:'center', gap:7 }}>
                  <Star size={15} color="#F59E0B" fill="#F59E0B" /> Reviews ({reviews.length})
                </h3>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {reviews.map((r, i) => (
                    <div key={i} style={{ padding:'11px 13px', background:'var(--gray-light)', borderRadius:10 }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:28, height:28, background:'linear-gradient(135deg,var(--orange),var(--amber))', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:700, fontSize:11 }}>{r.userName?.[0] || 'C'}</div>
                          <span style={{ fontWeight:600, fontSize:13 }}>{r.userName || 'Customer'}</span>
                        </div>
                        <div>{[1,2,3,4,5].map(s => <span key={s} style={{ color: s <= r.rating ? '#F59E0B' : '#D1D5DB', fontSize:13 }}>★</span>)}</div>
                      </div>
                      {r.comment && <p style={{ color:'var(--gray)', fontSize:12, lineHeight:1.5 }}>{r.comment}</p>}
                      <div style={{ fontSize:10, color:'var(--gray)', marginTop:4 }}>{new Date(r.date).toLocaleDateString('en-IN')}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <LocationPickerModal
          title="Your Location"
          onClose={() => setShowLocationPicker(false)}
          onSelect={(loc) => { saveLocation(loc); setShowLocationPicker(false); toast.success('Location updated!'); }}
        />
      )}
    </div>
  );
}

function ShopMaterialCard({ mat, expanded, onToggle, onAdd, onWishlist, cartCount, wishlisted }) {
  const hasVariants = Array.isArray(mat.variants) && mat.variants.length > 0;
  const productImage = mat.images?.[0];
  const minOrder = Number(mat.minimumOrder || 1);
  const categoryLabel = String(mat.category || 'Material').replace('_', ' ');

  const handleWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onWishlist(mat);
  };

  const handleAddClick = () => onAdd(mat);

  return (
    <article className="material-product-card shop-material-card">
      <Link to={`/materials/${mat._id}`} className="material-product-link">
        <div className="material-product-media">
          <span className="material-product-chip">{mat.brand || categoryLabel}</span>
          <button onClick={handleWishlistClick} className={`material-wishlist ${wishlisted ? 'active' : ''}`} title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}>
            <Heart size={15} fill={wishlisted ? 'currentColor' : 'none'} />
          </button>
          {productImage ? (
            <img src={productImage} alt={mat.name} />
          ) : (
            <div className="material-image-placeholder">
              <Package size={28} />
              <span>Product image</span>
            </div>
          )}
        </div>

        <div className="material-product-body">
          <div className="material-product-main-row">
            <div className="material-product-title-wrap">
              <h3>{mat.name}</h3>
              <div className="material-product-unit">{mat.unit || 'piece'}</div>
            </div>
            <div className="material-product-price">Rs {Number(mat.price || 0).toLocaleString('en-IN')}</div>
          </div>
          {mat.description && (
            <p className="shop-material-description">{mat.description}</p>
          )}
          <div className="material-meta-pills">
            <span>{Number(mat.stock || 0) > 0 ? `${Number(mat.stock).toLocaleString('en-IN')} avail.` : 'Out of stock'}</span>
            <span>Min. {Number(minOrder).toLocaleString('en-IN')} {mat.unit || 'units'}</span>
          </div>
        </div>
      </Link>

      <div className="material-card-actions">
        {hasVariants ? (
          <button onClick={onToggle} className="material-add-btn">
            {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />} Sizes
          </button>
        ) : (
          <button onClick={handleAddClick} disabled={mat.stock === 0} className="material-add-btn">
            <ShoppingCart size={16} /> {mat.stock === 0 ? 'Out' : 'Add'}
          </button>
        )}
        {cartCount > 0 && (
          <span className="shop-material-added-badge">
            <CheckCircle size={14} /> {cartCount} in cart
          </span>
        )}
      </div>

      {expanded && hasVariants && (
        <div className="shop-material-variants">
          {mat.variants.map((v, i) => (
            <div key={i} className="shop-material-variant-row">
              <div>
                <strong>{String(v.size || 'Variant')}</strong>
                <span>{String(v.unit || mat.unit || '')}</span>
              </div>
              <div>
                <b>Rs {Number(v.price || 0).toLocaleString('en-IN')}</b>
                <button onClick={() => onAdd(mat, v)} disabled={Number(v.stock) === 0}>Add</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function MaterialItem({ mat, expanded, onToggle, onAdd, onWishlist, cartCount, wishlisted }) {
  const color = CAT_COLOR[mat.category] || 'var(--orange)';
  const discountedPrice = mat.offer?.discount ? mat.price * (1 - mat.offer.discount / 100) : null;
  const hasVariants = Array.isArray(mat.variants) && mat.variants.length > 0;

  return (
    <div style={{ background:'white', borderRadius:14, boxShadow:'var(--shadow)', overflow:'hidden' }}>
      <div style={{ padding:'13px 15px', display:'flex', alignItems:'flex-start', gap:11 }}>
        <div style={{ width:46, height:46, background: color+'15', borderRadius:11, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>
          {CAT_EMOJI[mat.category] || '📦'}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:2 }}>
            <span style={{ fontWeight:700, fontSize:14 }}>{mat.name}</span>
            {mat.brand && <span style={{ color:'var(--gray)', fontSize:12 }}>· {mat.brand}</span>}
            {mat.offer?.discount ? <span className="badge badge-green">{mat.offer.discount}% OFF</span> : null}
          </div>
          {mat.description && <p style={{ color:'var(--gray)', fontSize:12, lineHeight:1.5, marginBottom:5, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>{mat.description}</p>}
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <div>
              {discountedPrice ? (
                <><span style={{ fontWeight:700, fontSize:16, color:'var(--orange)' }}>₹{Math.round(discountedPrice).toLocaleString()}</span>
                <span style={{ textDecoration:'line-through', color:'var(--gray)', fontSize:12, marginLeft:4 }}>₹{mat.price.toLocaleString()}</span></>
              ) : <span style={{ fontWeight:700, fontSize:16, color:'var(--orange)' }}>₹{mat.price.toLocaleString()}</span>}
              <span style={{ color:'var(--gray)', fontSize:12 }}> / {mat.unit}</span>
            </div>
            <span style={{ color: mat.stock > 0 ? 'var(--green)' : 'var(--red)', fontSize:12, fontWeight:600 }}>
              {mat.stock > 0 ? `${mat.stock} avail.` : 'Out of stock'}
            </span>
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:5, alignItems:'flex-end', flexShrink:0 }}>
          <button onClick={() => onWishlist(mat)}
            style={{ width:32, height:32, borderRadius:8, border:`1.5px solid ${wishlisted ? 'var(--red)' : 'var(--gray-border)'}`, background: wishlisted ? '#FEF2F2' : 'white', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <Heart size={14} color={wishlisted ? 'var(--red)' : 'var(--gray)'} fill={wishlisted ? 'var(--red)' : 'none'} />
          </button>
          {hasVariants ? (
            <button onClick={onToggle} style={{ display:'flex', alignItems:'center', gap:4, background:'var(--gray-light)', border:'none', borderRadius:7, padding:'6px 11px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              Sizes {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>
          ) : (
            <button onClick={() => onAdd(mat)} disabled={mat.stock === 0} className="btn-primary" style={{ padding:'6px 13px', fontSize:12 }}>
              <Plus size={12} /> Add
            </button>
          )}
          {cartCount > 0 && <span style={{ color:'var(--orange)', fontSize:11, fontWeight:700 }}>{cartCount} in cart</span>}
        </div>
      </div>

      {/* Variants */}
      {expanded && hasVariants && (
        <div style={{ borderTop:'1px solid var(--gray-border)', padding:'10px 15px', background:'#FAFAFA' }}>
          <div style={{ fontSize:10, fontWeight:700, color:'var(--gray)', textTransform:'uppercase', marginBottom:7 }}>Select Size / Variant</div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {mat.variants.map((v, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'white', borderRadius:9, padding:'8px 11px', border:'1px solid var(--gray-border)', flexWrap:'wrap', gap:7 }}>
                <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                  <span style={{ fontWeight:600, fontSize:13 }}>{String(v.size || '')}</span>
                  <span style={{ color:'var(--gray)', fontSize:11 }}>{String(v.unit || '')}</span>
                  {v.sku ? <span style={{ color:'var(--gray)', fontSize:10 }}>SKU: {String(v.sku)}</span> : null}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                  <span style={{ fontWeight:700, color:'var(--orange)', fontSize:14 }}>₹{Number(v.price || 0).toLocaleString()}</span>
                  <span style={{ fontSize:11, color: Number(v.stock) > 0 ? 'var(--green)' : 'var(--red)', fontWeight:600 }}>
                    {Number(v.stock) > 0 ? `${v.stock} in stock` : 'Out of stock'}
                  </span>
                  <button onClick={() => onAdd(mat, v)} disabled={Number(v.stock) === 0} className="btn-primary" style={{ padding:'5px 11px', fontSize:11 }}>
                    <Plus size={11} /> Add
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Specs */}
      {Array.isArray(mat.specifications) && mat.specifications.filter(s => s.value).length > 0 && (
        <div style={{ borderTop:'1px solid var(--gray-border)', padding:'7px 15px', display:'flex', flexWrap:'wrap', gap:5 }}>
          {mat.specifications.filter(s => s.value).map((s, i) => (
            <span key={i} className="tag" style={{ fontSize:11 }}>{String(s.key)}: {String(s.value)}</span>
          ))}
        </div>
      )}
    </div>
  );
}
